import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Compass, Radio, ShoppingBag, MessageSquare, User, Bell, LayoutGrid, Search, Shield, LayoutDashboard, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

const allTabs = [
  { to: "/", label: "Discover", icon: Compass, id: "discover" },
  { to: "/live", label: "Live", icon: Radio, id: "live", badge: "Soon" },
  { to: "/market", label: "Market", icon: ShoppingBag, id: "market" },
  { to: "/community", label: "Community", icon: Users, id: "community" },
  { to: "/inbox", label: "Inbox", icon: MessageSquare, id: "inbox" },
  { to: "/you", label: "You", icon: User, id: "you" },
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const { user, features } = useAuth();
  const tabs = allTabs.filter((t) => (t.id === "live" ? features.live : t.id === "market" ? features.shop : t.id === "community" ? features.community : true));
  const { count } = useCart();
  const [unread, setUnread] = useState(0);
  const loc = useLocation();
  const dark = loc.pathname.startsWith("/table");

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [user, loc.pathname]);

  return (
    <div className={`min-h-screen ${dark ? "bg-[#121110] text-[#F6F4EE]" : ""}`}>
      <header className={`sticky top-0 z-40 backdrop-blur-md border-b px-4 py-3 ${dark ? "bg-[#121110]/90 border-[#2A2825]" : "bg-[#F6F4EE]/90 border-[#D8D4C9]"}`} data-testid="app-header">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <span className={`mono text-[10px] tracking-[0.2em] px-1.5 py-0.5 border ${dark ? "border-[#C5A059] text-[#C5A059]" : "border-[#1A1917]"}`}>SH</span>
            <span className="serif text-2xl font-semibold leading-none">Shufflar</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 text-sm">
            {tabs.map((t) => (
              <NavLink key={t.id} to={t.to} end={t.to === "/"} data-testid={`nav-${t.id}`}
                className={({ isActive }) => `px-3 py-1.5 rounded-full transition-colors ${isActive ? (dark ? "bg-[#F6F4EE] text-[#121110]" : "bg-[#1A1917] text-[#F6F4EE]") : (dark ? "hover:bg-[#2A2825]" : "hover:bg-[#EFECE4]")}`}>
                {t.label}{t.badge && <span className="ml-1 mono text-[9px] text-[#C5A059]">{t.badge}</span>}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1.5">
            <Link to="/search" className="p-2 rounded-full hover:bg-[#EFECE4]/60" aria-label="Search" data-testid="header-search"><Search size={18} /></Link>
            <Link to="/table" className={`p-2 rounded-full ${dark ? "text-[#C5A059]" : "hover:bg-[#EFECE4]"}`} aria-label="The Table" data-testid="header-table"><LayoutGrid size={18} /></Link>
            {user && (user.role === "reader" || user.role === "admin" || user.role === "moderator") && (
              <Link to="/dashboard" className="p-2 rounded-full hover:bg-[#EFECE4]/60 hidden sm:block" aria-label="Dashboard" data-testid="header-dashboard"><LayoutDashboard size={18} /></Link>
            )}
            {user && (user.role === "admin" || user.role === "moderator") && (
              <Link to="/admin" className="p-2 rounded-full hover:bg-[#EFECE4]/60 hidden sm:block" aria-label="Admin" data-testid="header-admin"><Shield size={18} /></Link>
            )}
            <Link to="/notifications" className="relative p-2 rounded-full hover:bg-[#EFECE4]/60" aria-label="Notifications" data-testid="header-notifications">
              <Bell size={18} />
              {unread > 0 && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-[#581C25]" data-testid="notif-dot" />}
            </Link>
            <Link to="/cart" className="relative p-2 rounded-full hover:bg-[#EFECE4]/60" aria-label="Cart" data-testid="header-cart">
              <ShoppingBag size={18} />
              {count > 0 && <span className="absolute -top-0.5 -right-0.5 mono text-[9px] bg-[#581C25] text-[#F6F4EE] rounded-full h-4 w-4 flex items-center justify-center" data-testid="cart-count">{count}</span>}
            </Link>
            {user ? (
              <Link to="/you" className="ml-1 h-8 w-8 rounded-full bg-[#1A1917] text-[#F6F4EE] flex items-center justify-center overflow-hidden" data-testid="header-avatar">
                {user.avatar ? <img src={user.avatar.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar}` : user.avatar} alt="" className="h-full w-full object-cover" /> : <span className="serif text-sm">{user.name?.[0]}</span>}
              </Link>
            ) : (
              <Link to="/login" className="ml-1 btn-ghost !py-1.5 !px-4 text-xs" data-testid="header-login">Sign in</Link>
            )}
          </div>
        </div>
      </header>
      <main className="pb-24 md:pb-10">{children}</main>
      <nav className={`md:hidden fixed bottom-0 left-0 right-0 z-50 backdrop-blur-md border-t ${dark ? "bg-[#121110]/95 border-[#2A2825]" : "bg-[#F6F4EE]/95 border-[#D8D4C9]"}`} data-testid="bottom-nav">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
          {tabs.map((t) => (
            <NavLink key={t.id} to={t.to} end={t.to === "/"} data-testid={`bottom-nav-${t.id}`}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] mono uppercase tracking-wider relative ${isActive ? "text-[#581C25]" : "text-[#5E5C56]"}`}>
              <t.icon size={20} strokeWidth={1.75} />
              {t.label}
              {t.badge && <span className="absolute top-1.5 right-3 text-[8px] text-[#C5A059]">{t.badge}</span>}
              {t.id === "inbox" && unread > 0 && <span className="absolute top-2 right-5 h-1.5 w-1.5 rounded-full bg-[#581C25]" />}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export const Avatar = ({ u, size = 40 }: { u: any; size?: number }) => {
  const src = u?.avatar && u.avatar.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u.avatar}` : u?.avatar;
  return (
  <div className="rounded-full bg-[#1A1917] text-[#F6F4EE] flex items-center justify-center overflow-hidden shrink-0" style={{ width: size, height: size }}>
    {src ? <img src={src} alt={u.name} className="h-full w-full object-cover" /> : <span className="serif" style={{ fontSize: size / 2.2 }}>{u?.name?.[0] || "?"}</span>}
  </div>
  );
};

export const Empty = ({ title, hint }: { title: string; hint?: string }) => (
  <div className="card-tactile p-8 text-center" data-testid="empty-state">
    <p className="serif text-2xl">{title}</p>
    {hint && <p className="text-sm text-[#5E5C56] mt-2">{hint}</p>}
  </div>
);

export const Page = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 ${className}`}>{children}</div>
);
